import React from 'react'
import { ChartDataLabelsPlugin, ChartPlugin, DoughnutChartElement } from './chartJS'
import { ChartProps } from 'react-chartjs-2'
import { CHART_TOOLTIP_OPTIONS, ChartLabelColoring, ChartTitleWrapper } from './chartWrapper'
import { DonutChartProps, EmptyPiePlugin, SunburstSlice } from './pieChart'
import { ArcElement } from 'chart.js'

export type SunburstData = SunburstSlice[]
interface SunburstChartProps extends Omit<DonutChartProps, 'data'> {
  data: SunburstData
}

export const SunburstChart: React.FC<SunburstChartProps> = ({
  legend,
  title,
  data,
  centerIcon,
  className,
  onLabelClick,
  recreateOnDataChange = true,
}) => {
  const processedData = React.useMemo(() => {
    return {
      datasets: [
        {
          data: data.flatMap((d) => d[3]?.map((s) => s[1]) || [d[1]]),
          backgroundColor: data.flatMap((d) => d[3]?.map((s) => s[2]) || [d[2]]),
          borderColor: data.flatMap((d) => d[3]?.map((s) => '') || ['j_upper']),
          labels: data.flatMap((d) => (d[3] ? d[3].map((s) => s[0]) : [d[0]])),
        },
        {
          data: data.map((d) => d[1]),
          backgroundColor: data.map((d) => d[2]),
          borderColor: data.map((d) => (d[3] ? 'inner' : 'j_inner')),
          labels: data.map((d) => d[0]),
        },
      ],
    }
  }, [data])

  const noData = !processedData.datasets.some((d) => d.data.some((e) => e > 0))

  const processedOptions = React.useMemo(() => {
    const options: ChartProps<'doughnut'>['options'] = {
      backgroundColor: 'transparent',
      onClick: function (e, elements) {
        onLabelClick &&
          onLabelClick(
            elements.map((element) => processedData.datasets[element.datasetIndex].labels[element.index].toLowerCase())
          )
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          ...CHART_TOOLTIP_OPTIONS,
          callbacks: {
            label: function (context) {
              const dataset = context.datasetIndex
              const index = context.dataIndex
              const label = processedData.datasets[dataset].labels[index]
              const value = processedData.datasets[dataset].data[index]
              return `${label}: ${value}`
            },
          },
        },
        datalabels: {
          color: ChartLabelColoring({
            tinyValueThreshold: 1 / 150,
          }),
          font: function (context) {
            const thisVal = context.dataset.data[context.dataIndex] as number
            // @ts-ignore
            const borderColor = context.dataset.borderColor[context.dataIndex] as string
            if (borderColor == 'inner') {
              // inner ring, size can be bigger
              return {
                size: thisVal < 1000 ? 12 : thisVal < 10000 ? 10 : 8,
                weight: 'bold',
              }
            } else {
              // joined, plenty of room
              if (borderColor == 'j_inner')
                return {
                  size: 12,
                  weight: 'bold',
                }
              // narrow outer ring, size must be smaller
              return {
                size: thisVal < 100 ? 12 : thisVal < 1000 ? 10 : 7,
                weight: 'bold',
              }
            }
          },
          formatter: function (value, context) {
            //no label on middle section
            // @ts-ignore
            const borderColor = context.dataset.borderColor[context.dataIndex] as string
            if (borderColor === 'j_upper') return ''
            return value == 0 ? '' : '' + value
          },
          anchor: 'center',
          align: function (context) {
            // @ts-ignore
            const borderColor = context.dataset.borderColor[context.dataIndex] as string
            if (borderColor === 'j_inner') return 'end'
            return 'center'
          },
          offset: function (context) {
            // @ts-ignore
            const borderColor = context.dataset.borderColor[context.dataIndex] as string
            if (borderColor === 'j_inner') return (('' + context.dataset.data).length - 1) * -2
            return 4
          },
        },
      },
    }
    return options
  }, [data])

  const legendItems = React.useMemo(() => {
    if (!legend) return undefined
    return data.map((d) => ({
      label: d[0],
      color: d[2],
      subitems: (d[3] || []).map((s) => ({ label: s[0], color: s[2] })),
    }))
  }, [data, legend])

  // force recreate when data changes
  const chartKey = recreateOnDataChange ? React.useMemo(() => JSON.stringify(data), [data]) : ''

  return (
    <ChartTitleWrapper
      legendItems={legendItems}
      legend={legend}
      title={title}
      className={className}
      onLabelClick={onLabelClick}
      dummyLegend={noData}
    >
      <DoughnutChartElement
        key={chartKey}
        plugins={[EmptyPiePlugin, SunburstRingThicknessPlugin, ArcElementSunburstFix, ChartDataLabelsPlugin]}
        data={processedData}
        options={processedOptions}
        className="z-10"
      />
      {centerIcon && <div className="absolute inset-auto">{centerIcon}</div>}
    </ChartTitleWrapper>
  )
}

const SunburstRingThicknessPlugin: ChartPlugin<'doughnut'> = {
  id: 'sunburst-hack',
  beforeDraw: function (chart, options) {
    const ring_weights = [0.17, 0.33]
    let r = chart.width / 2
    let curr_outer = 0.5 * r
    for (let i = chart.getVisibleDatasetCount() - 1; i >= 0; i--) {
      let new_outer = curr_outer + ring_weights[i] * r
      if (i == 0 && new_outer < 1) new_outer = 1
      chart.getDatasetMeta(i).data.forEach((e) => {
        // @ts-ignore
        e.innerRadius = curr_outer
        // @ts-ignore
        e.outerRadius = new_outer
      })
      curr_outer = new_outer
    }
  },
}

const ArcElementSunburstFix: ChartPlugin<'doughnut'> = {
  id: 'arcElementSunburstFix',
  beforeDraw(chart, args, options) {
    ;(ArcElement.prototype as any).originalDraw = ArcElement.prototype.draw
    //allows for using a donut charts as a sunburst chart
    //kinda hacky since whether to draw borders is stored in "borderColor"
    ArcElement.prototype.draw = function (ctx) {
      let fullCircle = (this.endAngle - this.startAngle) % (Math.PI * 2) < 0.1
      ctx.save()
      ctx.fillStyle = this.options.backgroundColor
      this.options.borderColor
      ctx.strokeStyle = 'white'
      ctx.lineWidth = this.options.borderWidth
      const e = 3 //extra line length to remove artifacts
      const p1 = rThetaToXY(this.innerRadius, this.endAngle, this.x, this.y)
      const p1overshot = rThetaToXY(this.innerRadius - e, this.endAngle, this.x, this.y)
      const p2 = rThetaToXY(this.outerRadius, this.startAngle, this.x, this.y)
      const p2overshot = rThetaToXY(this.outerRadius + e, this.startAngle, this.x, this.y)
      const p3overshot = rThetaToXY(this.outerRadius + e, this.endAngle, this.x, this.y)
      const p0 = rThetaToXY(this.innerRadius, this.startAngle, this.x, this.y)
      const p0overshot = rThetaToXY(this.innerRadius - e, this.startAngle, this.x, this.y)

      if (fullCircle) {
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.outerRadius, this.startAngle, this.endAngle)
        if (this.innerRadius != 0) ctx.arc(this.x, this.y, this.innerRadius, this.endAngle, this.startAngle, true)
        ctx.fill()
      } else {
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.outerRadius, this.startAngle, this.endAngle)
        ctx.lineTo(p1.x, p1.y)
        ctx.arc(this.x, this.y, this.innerRadius, this.endAngle, this.startAngle, true)
        ctx.lineTo(p2.x, p2.y)
        ctx.fill()
      }
      if (this.options.borderColor == 'j_upper') {
        ctx.strokeStyle = this.options.backgroundColor
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.innerRadius, this.startAngle, this.endAngle)
        ctx.stroke()

        ctx.strokeStyle = 'white'
        ctx.beginPath()
        if (fullCircle) {
          ctx.arc(this.x, this.y, this.outerRadius, this.startAngle, this.endAngle)
        } else {
          ctx.moveTo(p0overshot.x, p0overshot.y)
          ctx.lineTo(p2.x, p2.y)
          ctx.arc(this.x, this.y, this.outerRadius, this.startAngle, this.endAngle)
          ctx.lineTo(p1overshot.x, p1overshot.y)
        }
        ctx.stroke()
      } else if (this.options.borderColor == 'j_inner') {
        ctx.strokeStyle = this.options.backgroundColor
        ctx.beginPath()
        ctx.arc(this.x, this.y, this.outerRadius, this.startAngle, this.endAngle)
        ctx.stroke()

        ctx.strokeStyle = 'white'
        ctx.beginPath()
        if (fullCircle) {
          ctx.arc(this.x, this.y, this.innerRadius, this.startAngle, this.endAngle)
        } else {
          ctx.moveTo(p2overshot.x, p2overshot.y)
          ctx.lineTo(p0.x, p0.y)
          ctx.arc(this.x, this.y, this.innerRadius, this.startAngle, this.endAngle)
          ctx.lineTo(p3overshot.x, p3overshot.y)
        }
        ctx.stroke()
      } else {
        if (fullCircle) {
          if (this.innerRadius > 0) {
            ctx.beginPath()
            ctx.arc(this.x, this.y, this.innerRadius, this.startAngle, this.endAngle)
            ctx.stroke()
          }
          ctx.beginPath()
          ctx.arc(this.x, this.y, this.outerRadius, this.startAngle, this.endAngle)
        }
        ctx.stroke()
      }

      ctx.restore()
    }
  },
  afterDraw(chart, args, options) {
    ArcElement.prototype.draw = (ArcElement.prototype as any).originalDraw
  },
}

function rThetaToXY(r: number, theta: number, x: number, y: number) {
  return {
    x: x + r * Math.cos(theta),
    y: y + r * Math.sin(theta),
  }
}
